import {SPACE_META_KEY, type PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {Turn} from '@microsoft/agent-host-protocol'
import {SpaceIcon} from '@primer/octicons-react'
import {use, useMemo} from 'react'
import {ReferenceChip} from '@github-ui/agent-sessions-interface/components/ReferenceChip'
import {ReferenceChipList} from '@github-ui/agent-sessions-interface/components/ReferenceChipList'
import {UserMessage} from '@github-ui/agent-sessions-interface/components/UserMessage'

import {ClickableAttachmentChip} from '../session-logs/UserMessageBubble'
import {MessageAttachmentClickContext} from '../session-logs/MessageAttachmentClickContext'
import {extractChipAttachments} from '../session-logs/ahp/AhpTurnView'
import {extractTurnSpace} from '../session-logs/ahp/turn-meta'
import {extractUserMessageImages} from '../session-logs/ahp/user-message-images'
import {UserMessageImages} from '../session-logs/UserMessageImages'

export function QuickChatProvisioningUserMessage({prompt}: {prompt: PreparedPrompt}) {
  const onAttachmentClick = use(MessageAttachmentClickContext)
  // Reuse the established turn splitters so a provisioning message shows the same
  // inline previews and reference chips the real timeline shows once it replaces this.
  // UserMessageBubble itself needs a session, which does not exist yet while provisioning.
  const message = useMemo<Turn['message']>(
    () =>
      ({
        attachments: prompt.attachments ? [...prompt.attachments] : [],
        ...(prompt.displaySpace ? {_meta: {[SPACE_META_KEY]: prompt.displaySpace}} : {}),
      }) as Turn['message'],
    [prompt.attachments, prompt.displaySpace],
  )
  const images = useMemo(() => extractUserMessageImages(message), [message])
  const chipAttachments = useMemo(() => extractChipAttachments(message), [message])
  const space = extractTurnSpace(message)

  return (
    <UserMessage
      text={<span className="wb-break-word">{prompt.text}</span>}
      media={images.length > 0 ? <UserMessageImages images={images} /> : undefined}
      references={
        space || chipAttachments.length > 0 ? (
          <ReferenceChipList
            ariaLabel="Attachments"
            items={[
              ...(space
                ? [
                    <ReferenceChip
                      key="space"
                      label={`Space: ${space.name}`}
                      wrapLabel
                      href={`/copilot/spaces/${encodeURIComponent(space.owner)}/${space.id}`}
                      leadingVisual={<SpaceIcon size={12} />}
                    />,
                  ]
                : []),
              ...chipAttachments.map((attachment, index) => (
                <ClickableAttachmentChip
                  // eslint-disable-next-line @eslint-react/no-array-index-key -- attachments are a static, order-stable list with no stable id
                  key={index}
                  attachment={attachment}
                  onClick={onAttachmentClick}
                />
              )),
            ]}
          />
        ) : undefined
      }
    />
  )
}
